import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

interface Team {
  id: number;
  name: string;
  league?: string;
  section?: string;
  league_start_date?: string;
  league_end_date?: string;
  use_first_match_for_start_date?: boolean;
  use_last_match_for_end_date?: boolean;
  created_by?: number;
}

interface TeamContextType {
  selectedTeamId: number | null;
  setSelectedTeamId: (id: number) => void;
  clearSelectedTeam: () => void;
  teams: Team[];
  loading: boolean;
  refreshTeams: () => Promise<void>;
  showTeamSelector: boolean;
  setShowTeamSelector: (show: boolean) => void;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

interface TeamProviderProps {
  children: ReactNode;
}

export const TeamProvider: React.FC<TeamProviderProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamIdState] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTeamSelector, setShowTeamSelector] = useState(false);

  // Fetch teams on mount and when user changes
  useEffect(() => {
    const fetchTeams = async () => {
      if (!user) {
        setTeams([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get('/teams/');
        const teamsData = response.data;
        
        // Sort teams by league_start_date in ascending order
        const sortedTeams = teamsData.sort((a: Team, b: Team) => {
          // Teams without league_start_date go to the end
          if (!a.league_start_date && !b.league_start_date) return 0;
          if (!a.league_start_date) return 1;
          if (!b.league_start_date) return -1;
          
          // Compare dates
          return new Date(a.league_start_date).getTime() - new Date(b.league_start_date).getTime();
        });
        
        setTeams(sortedTeams);

        // Handle team selection
        if (teamsData.length > 0) {
          const savedTeamId = localStorage.getItem('selectedTeamId');
          const savedTeamIdNum = savedTeamId ? parseInt(savedTeamId) : null;

          // Check if saved team exists in user's teams
          const savedTeamExists = savedTeamIdNum && teamsData.some((t: Team) => t.id === savedTeamIdNum);

          if (savedTeamExists) {
            setSelectedTeamIdState(savedTeamIdNum);
          } else {
            // Don't automatically show team selector - let users select teams when needed
            // Team selection is only required on the Manage Teams page
            setSelectedTeamIdState(null);
          }
        } else {
          // No teams available
          setSelectedTeamIdState(null);
          localStorage.removeItem('selectedTeamId');
        }
      } catch (error) {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchTeams();
    }
  }, [user, authLoading]);

  const refreshTeams = useCallback(async () => {
    if (!user) {
      setTeams([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get('/teams/');
      const teamsData = response.data;
      
      // Sort teams by league_start_date in ascending order
      const sortedTeams = teamsData.sort((a: Team, b: Team) => {
        // Teams without league_start_date go to the end
        if (!a.league_start_date && !b.league_start_date) return 0;
        if (!a.league_start_date) return 1;
        if (!b.league_start_date) return -1;
        
        // Compare dates
        return new Date(a.league_start_date).getTime() - new Date(b.league_start_date).getTime();
      });
      
      setTeams(sortedTeams);

      // Handle team selection
      if (teamsData.length > 0) {
        const savedTeamId = localStorage.getItem('selectedTeamId');
        const savedTeamIdNum = savedTeamId ? parseInt(savedTeamId) : null;

        // Check if saved team exists in user's teams
        const savedTeamExists = savedTeamIdNum && teamsData.some((t: Team) => t.id === savedTeamIdNum);

        if (savedTeamExists) {
          setSelectedTeamIdState(savedTeamIdNum);
        }
      } else {
        // No teams available
        setSelectedTeamIdState(null);
        localStorage.removeItem('selectedTeamId');
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setSelectedTeamId = useCallback((id: number) => {
    setSelectedTeamIdState(id);
    localStorage.setItem('selectedTeamId', id.toString());
    setShowTeamSelector(false);
  }, []);

  const clearSelectedTeam = useCallback(() => {
    setSelectedTeamIdState(null);
    localStorage.removeItem('selectedTeamId');
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    selectedTeamId,
    setSelectedTeamId,
    clearSelectedTeam,
    teams,
    loading,
    refreshTeams,
    showTeamSelector,
    setShowTeamSelector,
  }), [selectedTeamId, setSelectedTeamId, clearSelectedTeam, teams, loading, refreshTeams, showTeamSelector, setShowTeamSelector]);

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

